import { Group, Text, UnstyledButton } from '@mantine/core';
import { CSSProperties, ReactElement, ReactNode } from 'react';

/**
 * The foot of a hub tab: the count, and paging at 100.
 *
 * With `total` it reads `Showing n of N patients`, N being what matches rather
 * than what was fetched. A tab still loading its whole cohort passes no total
 * and gets the plain count, no pager.
 */
export interface QueueFooterProps {
  /** Rows on screen after the in-memory filters. */
  shown: number;
  total?: number;
  page?: number;
  pageCount?: number;
  onPageChange?: (page: number) => void;
  /** Replaces the `Showing n of N patients` text, for a tab whose rows aren't patients. */
  summary?: ReactNode;
}

const FOOT_STYLE: CSSProperties = {
  padding: '10px 12px',
  background: '#fff',
  borderTop: '1px solid rgba(0, 0, 0, 0.10)',
};

const GREY = 'rgba(0, 0, 0, 0.55)';

function pagerButtonStyle(disabled: boolean): CSSProperties {
  return {
    padding: '5px 10px',
    borderRadius: 8,
    border: '1px solid rgba(0, 0, 0, 0.10)',
    background: '#fff',
    fontSize: 12,
    lineHeight: '16px',
    color: disabled ? 'rgba(0, 0, 0, 0.35)' : 'var(--mantine-color-black, #000)',
    cursor: disabled ? 'default' : 'pointer',
  };
}

export function Count({ value }: { value: number }): ReactElement {
  return (
    <Text component="b" fz={12} fw={600} c="black">
      {value.toLocaleString('en-US')}
    </Text>
  );
}

export function QueueFooter({
  shown,
  total,
  page = 1,
  pageCount = 1,
  onPageChange,
  summary,
}: QueueFooterProps): ReactElement {
  const showPager = onPageChange !== undefined && pageCount > 1;

  return (
    <Group justify="space-between" gap={12} wrap="wrap" style={FOOT_STYLE}>
      <Text fz={12} c={GREY}>
        {summary ?? (
          <>
            Showing <Count value={shown} />
            {total !== undefined && (
              <>
                {' of '}
                <Count value={total} />
              </>
            )}{' '}
            patients
          </>
        )}
      </Text>
      {showPager && (
        <Group gap={4} wrap="nowrap">
          <UnstyledButton
            disabled={page <= 1}
            style={pagerButtonStyle(page <= 1)}
            onClick={() => onPageChange(page - 1)}
          >
            Previous
          </UnstyledButton>
          <Text fz={12} c={GREY} px={6} style={{ fontVariantNumeric: 'tabular-nums' }}>
            Page {page} of {pageCount}
          </Text>
          <UnstyledButton
            disabled={page >= pageCount}
            style={pagerButtonStyle(page >= pageCount)}
            onClick={() => onPageChange(page + 1)}
          >
            Next
          </UnstyledButton>
        </Group>
      )}
    </Group>
  );
}
